import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Search, TriangleAlert, X } from "lucide-react";
import { SYMPTOMS, SYMPTOM_DISCLAIMER } from "@/data/symptoms";
import { getOrgan } from "@/data/organs";
import { elementColor } from "@/data/theme";
import { PageHeader, SafetyNote, SourceTag } from "@/components/common";

const normalizeText = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

function matchSymptom(s, query) {
  if (!query) return true;
  const normQuery = normalizeText(query);

  const sintomaText = normalizeText(s.sintoma);
  const aliasesText = normalizeText((s.aliases || []).join(" "));
  const relacoesText = normalizeText(s.relacoes);
  const fullTarget = `${sintomaText} ${aliasesText} ${relacoesText}`;

  // 1. Frase exata direta
  if (fullTarget.includes(normQuery)) return true;

  // 2. Expansão coloquial (ex: xixi -> urina)
  if (normQuery.includes("xixi")) {
    const expandedQuery = normQuery.replace(/\bxixi\b/g, "urina");
    if (fullTarget.includes(expandedQuery)) return true;
  }

  // 3. Correspondência de termos individuais (ignorando preposições/artigos curtos)
  const stopWords = new Set(["de", "da", "do", "das", "dos", "em", "na", "no", "nas", "nos", "e", "ou", "com", "ao", "a", "o"]);
  const words = normQuery.split(/\s+/).filter((w) => w && !stopWords.has(w));
  if (words.length > 0) {
    const allWordsMatch = words.every((w) => {
      const altW = w === "xixi" ? "urina" : w;
      const re = new RegExp(`\\b${w}\\b|\\b${altW}\\b`);
      return re.test(fullTarget);
    });
    if (allWordsMatch) return true;
  }

  return false;
}

export default function Symptoms() {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredSymptoms = searchTerm
    ? SYMPTOMS.filter((s) => matchSymptom(s, searchTerm))
    : SYMPTOMS;

  return (
    <div className="space-y-10">
      <PageHeader
        eyebrow="Mapa rápido por sintoma"
        title="Estou sentindo algo"
        subtitle="Encontre o caminho pelo que você sente. Ao escolher um sintoma, o guia mostra apenas as possíveis relações indicadas na Medicina Tradicional Chinesa — nunca um diagnóstico."
      />

      <SafetyNote tone="warn">{SYMPTOM_DISCLAIMER} Este recurso não é diagnóstico, não indica probabilidades e não substitui avaliação profissional.</SafetyNote>

      {/* Campo de filtro rápido */}
      <div className="relative w-full max-w-xl">
        <label htmlFor="symptom-search" className="sr-only">
          Buscar sintoma ou sinal
        </label>
        <div className="relative flex items-center">
          <Search className="absolute left-4 h-5 w-5 text-muted-foreground pointer-events-none" strokeWidth={1.75} />
          <input
            id="symptom-search"
            data-testid="symptom-search-input"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Busque um sintoma ou sinal..."
            aria-label="Busque um sintoma ou sinal..."
            className="w-full rounded-2xl border border-input bg-card py-3.5 pl-11 pr-11 text-sm sm:text-base text-foreground placeholder:text-muted-foreground/70 outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/25"
          />
          {searchTerm && (
            <button
              type="button"
              data-testid="clear-symptom-search"
              onClick={() => setSearchTerm("")}
              aria-label="Limpar busca"
              className="absolute right-3.5 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {searchTerm.trim() && (
          <p className="mt-2 text-xs text-muted-foreground px-1" data-testid="symptom-search-count">
            {filteredSymptoms.length === 1
              ? "1 sintoma encontrado"
              : `${filteredSymptoms.length} sintomas encontrados`}
          </p>
        )}
      </div>

      {filteredSymptoms.length === 0 ? (
        <div
          data-testid="symptom-empty-state"
          className="rounded-3xl border border-dashed border-border bg-card/60 p-8 sm:p-12 text-center space-y-4"
        >
          <p className="text-base text-muted-foreground">
            Nenhum resultado encontrado para esta busca.
          </p>
          <button
            type="button"
            data-testid="symptom-clear-button"
            onClick={() => setSearchTerm("")}
            className="inline-flex items-center rounded-full border border-gold/40 px-5 py-2 text-sm font-medium text-foreground hover:bg-secondary transition-colors"
          >
            Limpar busca
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {filteredSymptoms.map((s) => (
            <div
              key={s.id}
              data-testid={`symptom-card-${s.id}`}
              className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-gold/40 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg font-medium text-foreground leading-snug">{s.sintoma}</h3>
                  {s.sourcePages && <SourceTag pages={s.sourcePages} className="shrink-0 mt-1" />}
                </div>

                {s.aliases && s.aliases.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    <span className="text-muted-foreground/80 font-medium">Buscas relacionadas:</span> {s.aliases.join(", ")}
                  </p>
                )}

                <p className="mt-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Possíveis relações na MTC</p>
                <p className="text-sm font-medium text-foreground leading-snug">{s.relacoes}</p>

                {s.contexto && (
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{s.contexto}</p>
                )}

                {s.alerta && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl border border-destructive/25 bg-destructive/5 p-2.5 text-xs text-muted-foreground leading-relaxed">
                    <TriangleAlert className="h-4 w-4 shrink-0 text-destructive/80 mt-0.5" />
                    <span>{s.alerta}</span>
                  </div>
                )}
              </div>

              <div className="mt-5 flex flex-wrap gap-2 pt-1 border-t border-border/40">
                {s.relogio ? (
                  <Link
                    to="/relogio"
                    data-testid={`symptom-${s.id}-relogio`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-sm font-medium text-foreground hover:bg-gold/20 transition-colors"
                  >
                    <Clock className="h-4 w-4" /> Relógio dos Órgãos
                  </Link>
                ) : (
                  s.orgaoIds.map((oid) => {
                    const o = getOrgan(oid);
                    if (!o) return null;
                    const c = elementColor(o.elemento);
                    return (
                      <Link
                        key={oid}
                        to={`/orgaos/${oid}`}
                        data-testid={`symptom-${s.id}-organ-${oid}`}
                        className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs sm:text-sm font-medium transition-transform hover:scale-105"
                        style={{ backgroundColor: c.soft, color: c.text }}
                      >
                        {o.name} <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-border bg-secondary/50 p-6">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Observe com calma. Consulte com consciência. Cuide-se com gentileza. Você também pode explorar por tema:{" "}
          <Link to="/orgaos" className="text-gold font-medium hover:underline">Órgãos</Link>,{" "}
          <Link to="/elementos" className="text-gold font-medium hover:underline">Cinco Elementos</Link>,{" "}
          <Link to="/emocoes" className="text-gold font-medium hover:underline">Emoções</Link> e{" "}
          <Link to="/autocuidado" className="text-gold font-medium hover:underline">Práticas de autocuidado</Link>.
        </p>
      </div>
    </div>
  );
}
